import { cloneDeep, isObject, mapValues, startCase } from 'es-toolkit/compat';

/**
 * Set all keys to a value
 * @param object - object to set all keys
 * @param value - value to set all keys
 * @returns object with all keys set to value
 */
export const setAllKeys = <T extends Record<string, boolean>, U>(object: T, value: U): Record<keyof T, U> =>
  mapValues(cloneDeep(object), () => value);

export const ProjectPermissionsRoles = {
  edit: 'Editor',
  owner: 'Owner',
  view: 'Viewer',
} as const;

/**
 * Format the action string from a traffic project permissions object.
 * For formating user permissions use formatUserPermissionString.
 * @param permission - the permission string to format.
 * @returns the formatted action string.
 */
export const formatPermissionString = (permission: string) => {
  const actionLowerCase = permission.toLowerCase();
  if (permission === 'edit') return ProjectPermissionsRoles.edit;
  if (permission === 'view') return ProjectPermissionsRoles.view;
  return startCase(actionLowerCase);
};

/**
 * Format the action string from a traffic project permissions object.
 * @param permission - the action string from a traffic project permissions object.
 * @param alias - the alias of the user that the permission is for.
 * @param projectOwner - the alias of the project owner.
 * @returns the formatted action string.
 */
export const formatUserPermissionString = (permission: string, alias: string, projectOwner: string) => {
  const actionLowerCase = permission.toLowerCase();
  if (alias.toLowerCase() === projectOwner.toLowerCase()) return ProjectPermissionsRoles.owner;
  return formatPermissionString(actionLowerCase);
};

type UnionOmit<T, K extends string | number | symbol> = T extends unknown ? Omit<T, K> : never;
type NullUnionOmit<T, K extends string | number | symbol> = null extends T ? UnionOmit<NonNullable<T>, K> : UnionOmit<T, K>;

type RecursiveOmitHelper<T, K extends string | number | symbol> = {
  [P in keyof T]: RecursiveOmit<T[P], K>;
};
type RecursiveOmit<T, K extends string | number | symbol> = T extends {
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  [P in K]: any;
}
  ? NullUnionOmit<RecursiveOmitHelper<T, K>, K>
  : RecursiveOmitHelper<T, K>;

/**
 * Omit the __typename key from an object
 * @param obj - object to omit typename from
 * @returns object with typename omitted
 */
export const deepOmitTypename = <T>(obj: T): RecursiveOmit<T, '__typename'> => {
  if (Array.isArray(obj)) {
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    return obj.map(deepOmitTypename) as any;
  }
  if (isObject(obj) && obj !== null) {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([key]) => key !== '__typename')
        .map(([key, value]) => [
          key,
          deepOmitTypename(value),
        ]),
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
    ) as any;
  }
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  return obj as any;
};
